<script setup lang="ts">
import type { Instance } from '@popperjs/core';
import { createPopper } from '@popperjs/core';

const showEvents = ['mouseenter', 'focus'];
const hideEvents = ['mouseleave', 'blur'];

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiTooltipProps {
  config?: any;
  useDefaultStyles?: boolean;
}
const props = withDefaults(defineProps<CiTooltipProps>(), {
  useDefaultStyles: true,
  config: () => ({
    placement: 'top',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'right'],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  }),
});

/********************
 * REFS & VARS       *
 ********************/
const popper = ref<Instance | null>(null);
const root = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);

/********************
 * WATCHERS          *
 ********************/
watch(() => props.config, onConfigChange, { deep: true });

onMounted(() => {
  nextTick(() => {
    if (triggerRef.value && tooltipRef.value) {
      popper.value = createPopper(triggerRef.value, tooltipRef.value, props.config);

      if (root.value) {
        showEvents.forEach((event) => {
          root.value!.addEventListener(event, show.bind(this));
        });

        hideEvents.forEach((event) => {
          root.value!.addEventListener(event, hide.bind(this));
        });
      }
    }
  });
});

onBeforeUnmount(() => {
  if (root.value) {
    showEvents.forEach((event) => {
      root.value!.removeEventListener(event, show.bind(this));
    });

    hideEvents.forEach((event) => {
      root.value!.removeEventListener(event, hide.bind(this));
    });
  }

  if (!popper.value) {
    return;
  }

  popper.value.destroy();
});

/********************
 * FUNCTIONS         *
 ********************/

function onConfigChange(newValue) {
  popper.value?.setOptions(newValue);
}

function show() {
  tooltipRef.value?.setAttribute('data-show', '');
  popper.value?.update();
}

function hide() {
  tooltipRef.value?.removeAttribute('data-show');
}
</script>

<template>
  <span ref="root">
    <span ref="triggerRef">
      <slot />
    </span>
    <span
      ref="tooltipRef"
      data-tooltip
      class="z-20"
      :class="{ tooltip: props.useDefaultStyles }"
    >
      <slot name="tooltip" />
      <div id="arrow" data-popper-arrow />
    </span>
  </span>
</template>

<style lang="postcss" scoped>
.tooltip {
  @apply w-auto hidden text-dark p-4 bg-[#fefbdd] rounded-lg border-2 border-yellow;
}

.tooltip[data-show] {
  @apply block visible;
}

#arrow {
  @apply absolute invisible;
}

#arrow::after,
#arrow::before {
  @apply absolute visible w-full content-[''];
}

.tooltip[data-popper-placement^='top'] > #arrow {
  @apply bottom-0;
}

.tooltip[data-popper-placement^='top'] > #arrow::after,
.tooltip[data-popper-placement^='top'] > #arrow::before {
  @apply top-0 left-0 ml-[-10px];
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
}

.tooltip[data-popper-placement^='top'] > #arrow::before {
  border-top: 10px solid #fcf5a4;
}

.tooltip[data-popper-placement^='top'] > #arrow::after {
  @apply -mt-0.5;
  border-top: 10px solid #fefbdd;
}

.tooltip[data-popper-placement^='bottom'] > #arrow {
  @apply top-0;
}

.tooltip[data-popper-placement^='bottom'] > #arrow::after,
.tooltip[data-popper-placement^='bottom'] > #arrow::before {
  @apply ml-[-10px] left-0 top-[-10px];
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
}

.tooltip[data-popper-placement^='bottom'] > #arrow::before {
  border-bottom: 10px solid #fefbdd;
  z-index: 1;
}

.tooltip[data-popper-placement^='bottom'] > #arrow::after {
  @apply -mt-0.5;
  border-bottom: 10px solid #fcf5a4;
  z-index: -1;
}

.tooltip[data-popper-placement^='left'] > #arrow {
  @apply right-0;
}

.tooltip[data-popper-placement^='left'] > #arrow::after,
.tooltip[data-popper-placement^='left'] > #arrow::before {
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  transform: translateY(-50%);
}

.tooltip[data-popper-placement^='left'] > #arrow::before {
  border-left: 10px solid #fcf5a4;
}

.tooltip[data-popper-placement^='left'] > #arrow::after {
  @apply -ml-0.5;
  border-left: 10px solid #fefbdd;
}

.tooltip[data-popper-placement^='right'] > #arrow {
  @apply left-[-10px];
}

.tooltip[data-popper-placement^='right'] > #arrow::after,
.tooltip[data-popper-placement^='right'] > #arrow::before {
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  transform: translateY(-50%);
}

.tooltip[data-popper-placement^='right'] > #arrow::before {
  border-right: 10px solid #fcf5a4;
}

.tooltip[data-popper-placement^='right'] > #arrow::after {
  @apply ml-0.5;
  border-right: 10px solid #fefbdd;
}
</style>
